<template>
  <div class="controls-container">
    <div class="control-field" v-if="databaseStore.hasMultiple">
      <span class="control-label">Database</span>
      <select
        class="db-select"
        :value="databaseStore.selected"
        :disabled="spin"
        @change="handleDatabaseChange"
      >
        <option v-for="db in databaseStore.databases" :key="db" :value="db">{{ db }}</option>
      </select>
    </div>

    <div class="control-field">
      <span class="control-label">Period</span>
      <DateRangePicker
        :model-value="range"
        :disabled="spin"
        @update:model-value="handleRangeChange"
      />
    </div>

    <GroupFilterDropdown
      v-if="processGroupFilter"
      :groups="processGroupFilter.groups"
      :excluded="processGroupFilter.excluded"
      :label="processGroupFilter.label"
      :has-ungrouped="processGroupFilter.hasUngrouped"
      @update:excluded="emit('update:processExcluded', $event)"
    />

    <GroupFilterDropdown
      v-if="queueGroupFilter"
      :groups="queueGroupFilter.groups"
      :excluded="queueGroupFilter.excluded"
      :label="queueGroupFilter.label"
      :has-ungrouped="queueGroupFilter.hasUngrouped"
      @update:excluded="emit('update:queueExcluded', $event)"
    />

    <ButtonSpinner
      class="reload-button"
      title="Refresh"
      :spin-while="spin"
      @click.stop="handleReload"
    />
  </div>
</template>

<script setup>
import DateRangePicker from '@/components/DateRangePicker.vue'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import GroupFilterDropdown from '@/Analytics/components/GroupFilterDropdown.vue'
import { useDatabaseStore } from '@/stores/useDatabaseStore'

const emit = defineEmits(['updateData', 'update:processExcluded', 'update:queueExcluded', 'databaseChange'])

// Shared across analytics pages. The dropdown only renders for users with more
// than one accessible database; everyone else is unaffected.
const databaseStore = useDatabaseStore()

const handleDatabaseChange = (event) => {
  databaseStore.setSelected(event.target.value)
  emit('databaseChange', event.target.value)
}

const props = defineProps({
  range: {
    type: Object,
    required: true
  },
  spin: {
    type: Boolean,
    required: true
  },
  processGroupFilter: {
    type: Object,
    default: null
  },
  queueGroupFilter: {
    type: Object,
    default: null
  }
})

const handleRangeChange = (newRange) => {
  emit('updateData', newRange)
}

const handleReload = () => {
  emit('updateData', props.range)
}
</script>

<style scoped>
.controls-container {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 12px 16px;
  background: var(--color-background);
  border: var(--border-card);
  border-radius: var(--border-radius-card);
  box-shadow: var(--shadow-card);
}

.control-field {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.control-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text);
  opacity: 0.55;
}

.db-select {
  padding: 0.45rem 0.75rem;
  border: var(--border-card);
  border-radius: var(--border-radius-card);
  font-size: 14px;
  color: var(--color-text);
  background: var(--color-background);
  cursor: pointer;
}

.db-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reload-button {
  margin-left: auto;
}
</style>
